import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, EMPTY, forkJoin, Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { Person, PersonAdapter } from '../data/persons/person.model';
import {
  Organization,
  OrganizationAdapter,
} from '../data/providers/organization.model';
import {
  PractitionerProvider,
  PractitionerProviderAdapter,
} from '../data/providers/practitioner-provider.model';
import {
  userOrganizationFetched,
  userOrganizationProvidersFetched,
  userPersonFetched,
  userPractitionerProvidersFetched,
} from '../store/actions/user-fhir.actions';
import {
  userFhirIdentifierSystemSelector,
  userFhirIdentifierValueSelector,
  userFhirIdSelector,
  userFhirReferenceSelector,
  userFhirResourceTypeSelector,
  userOrganizationFhirIdentifierValueSelector,
  userOrganizationFhirIdSelector,
  userOrganizationFhirReferenceSelector,
  userOrganizationSelector,
  userPersonSelector,
  userPractitionerProvidersSelector,
} from '../store/selectors/user-fhir.selectors';
import { secondFactorStateSelector } from '../store/selectors/session.selectors';
import { userPartnerCodeSelector } from '../store/selectors/user-profile.selectors';
import { isUserRoleProviderSelector } from '../store/selectors/user-role.selectors';
import { FhirApiService } from './api/fhir-api.sevice';
import { Reference } from 'fhir/r4';
import { SecondFactorState } from '../models/second-factor.state';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  resourceId$: Observable<string>;
  resourceType$: Observable<string>;
  partnerCode$: Observable<string>;
  userReference$: Observable<Reference | undefined> = of(undefined);
  defaultPageSize$ = of(10);
  currentLang$ = of('de');

  private resourceIdentifier$: Observable<string>;
  private resourceIdentifierSystem$: Observable<string>;
  private organizationResourceId$: Observable<string>;
  private organizationResourceIdentifier$: Observable<string>;

  private userPerson$: Observable<Person>;
  private isProvider$: Observable<boolean | null>;

  constructor(
    private store: Store,
    private fhirApi: FhirApiService,
    private personAdapter: PersonAdapter,
    private organizationAdapter: OrganizationAdapter,
    private practitionerProviderAdapter: PractitionerProviderAdapter,
  ) {
    this.resourceId$ = this.store.pipe(select(userFhirIdSelector));
    this.resourceIdentifier$ = this.store.pipe(
      select(userFhirIdentifierValueSelector),
    );
    this.resourceIdentifierSystem$ = this.store.pipe(
      select(userFhirIdentifierSystemSelector),
    );
    this.organizationResourceId$ = this.store.pipe(
      select(userOrganizationFhirIdSelector),
    );
    this.organizationResourceIdentifier$ = this.store.pipe(
      select(userOrganizationFhirIdentifierValueSelector),
    );

    this.resourceType$ = this.store.pipe(select(userFhirResourceTypeSelector));

    this.partnerCode$ = this.store.pipe(select(userPartnerCodeSelector));

    this.userPerson$ = this.store.pipe(select(userPersonSelector));
    this.isProvider$ = this.store.pipe(select(isUserRoleProviderSelector));

    if (!document.location.href.includes('/two-factor/recovery')) {
      this.fetchUserFhirOrganization();
    }

    this.initializeFhirDataAfterFullAuth();
  }

  /**
   * Initialize FHIR data fetching only after full authentication (2FA completed)
   */
  private initializeFhirDataAfterFullAuth(): void {
    this.store
      .pipe(
        select(secondFactorStateSelector),
        filter((state) => {
          return state === SecondFactorState.enforced;
        }),
        take(1),
      )
      .subscribe(() => {
        this.fetchUserFhirResource();
        this.fetchUserPractitionerProviders();
        this.fetchUserOrganizationProviders();
      });
  }

  /**
   * returns fhir reference to current User
   * @returns fhir4.Reference
   */
  public getUserReference(): fhir4.Reference {
    let _reference;
    this.store
      .pipe(select(userFhirReferenceSelector))
      .subscribe((reference) => (_reference = reference));

    return _reference;
  }

  /**
   * returns fhir reference to current User
   * @returns fhir4.Reference
   */
  public getUserOrganizationReference(): fhir4.Reference {
    let _reference;
    this.store
      .pipe(select(userOrganizationFhirReferenceSelector))
      .subscribe((reference) => (_reference = reference));

    return _reference;
  }

  /**
   * returns user Person
   * @returns Person
   */
  public getUserPerson(): Person {
    let _person;
    this.store
      .pipe(select(userPersonSelector))
      .subscribe((person) => (_person = person));

    return _person;
  }

  /**
   * returns user Organization
   * @returns Organization
   */
  public getUserOrganization(): Organization {
    let _organization;
    this.store
      .pipe(select(userOrganizationSelector))
      .subscribe((organization) => (_organization = organization));

    return _organization;
  }

  /**
   * returns user Identifier
   * @returns string
   */
  public getUserIdentifier(): string {
    let _identifier;
    this.resourceIdentifier$.subscribe(
      (identifier) => (_identifier = identifier),
    );

    return _identifier;
  }

  getCurrentLanguage() {
    return 'de';
  }

  getPatientId$(): Observable<string> {
    return EMPTY;
  }

  getUserPerson$(): Observable<Person> {
    throw new Error('Method not implemented.');
  }

  /**
   * returns user Identifier system
   * @returns string
   */
  public getUserIdentifierSystem(): string {
    let _system;
    this.resourceIdentifierSystem$.subscribe((system) => (_system = system));

    return _system;
  }

  /**
   * fetch, process and store user fhir resource
   */
  private fetchUserFhirResource(): void {
    combineLatest([
      this.resourceType$,
      this.userPerson$,
      this.resourceId$,
      this.resourceIdentifier$,
      this.resourceIdentifierSystem$,
    ]).subscribe(
      ([
        resourceType,
        userPersonInStore,
        resourceId,
        resourceIdentifier,
        resourceIdentifierSystem,
      ]) => {
        if (!resourceType || userPersonInStore) return;

        let resource$;

        if (resourceId && !resourceIdentifier) {
          resource$ = this.fhirApi.read(
            resourceType,
            resourceId,
            'user-profil',
          );
        } else if (resourceIdentifier && resourceIdentifierSystem) {
          const identifier = `${resourceIdentifierSystem}|${resourceIdentifier}`;

          resource$ = this.fhirApi.search(resourceType, { identifier }).pipe(
            map((bundle: fhir4.Bundle) => {
              return bundle?.entry?.[0]?.resource;
            }),
          );
        } else return;

        resource$.subscribe(
          (userFhirResource: fhir4.Patient | fhir4.Practitioner) => {
            if (!userFhirResource) return;

            this.personAdapter
              .fromResource$(userFhirResource)
              .subscribe((userPerson) =>
                this.store.dispatch(userPersonFetched({ userPerson })),
              );
          },
        );
      },
    );
  }

  /**
   * fetch, process and store user fhir organization
   */
  private fetchUserFhirOrganization(): void {
    combineLatest([
      this.organizationResourceId$,
      this.organizationResourceIdentifier$,
    ]).subscribe(([organizationResourceId, organizationResourceIdentifier]) => {
      let resource$;

      if (organizationResourceId && !organizationResourceIdentifier) {
        resource$ = this.fhirApi.read(
          'Organization',
          organizationResourceId,
          'user-profil',
        );
      } else if (organizationResourceIdentifier) {
        resource$ = this.fhirApi
          .search('Organization', {
            identifier: organizationResourceIdentifier,
          })
          .pipe(
            map((bundle: fhir4.Bundle) => {
              return bundle?.entry?.[0]?.resource;
            }),
          );
      } else return;

      resource$.subscribe(
        (userFhirOrganizationResource: fhir4.Organization) => {
          if (!userFhirOrganizationResource) return;

          this.organizationAdapter
            .fromResource$(userFhirOrganizationResource)
            .subscribe((userOrganization) =>
              this.store.dispatch(
                userOrganizationFetched({ userOrganization }),
              ),
            );
        },
      );
    });
  }

  /**
   * fetch, process and store user's PractitionerProviders (Fhir PractitionerRole) if user is provider
   */
  private fetchUserPractitionerProviders(): void {
    combineLatest([
      this.isProvider$,
      this.resourceIdentifier$,
      this.resourceIdentifierSystem$,
    ]).subscribe(
      ([isProvider, resourceIdentifier, resourceIdentifierSystem]) => {
        if (!isProvider || !resourceIdentifier || !resourceIdentifierSystem)
          return;

        const identifier = `${resourceIdentifierSystem}|${resourceIdentifier}`;

        this.fhirApi
          .search('PractitionerRole', {
            'practitioner.identifier': identifier,
            'organization.active': true,
            _include: '*',
          })
          .subscribe((bundle: fhir4.Bundle) => {
            forkJoin(
              (bundle?.entry || [])
                .filter(
                  ({ search, resource }) =>
                    search.mode === 'match' &&
                    resource.resourceType === 'PractitionerRole',
                )
                .map(({ resource }) =>
                  this.practitionerProviderAdapter.fromResource$(
                    resource as fhir4.PractitionerRole,
                    bundle,
                  ),
                ),
            ).subscribe((userPractitionerProviders: PractitionerProvider[]) =>
              this.store.dispatch(
                userPractitionerProvidersFetched({ userPractitionerProviders }),
              ),
            );
          });
      },
    );
  }

  /**
   * fetch, process and store user's Organizations incl. Organizations of higher level (Fhir Organizations from user's PractitionerRoles) if user is provider
   */
  private fetchUserOrganizationProviders(): void {
    this.store
      .pipe(select(userPractitionerProvidersSelector))
      .subscribe((userPractitionerProviders: PractitionerProvider[]) => {
        if (!userPractitionerProviders?.length) return;

        const organizationIds = userPractitionerProviders.map(
          ({ organization }) => organization.id,
        );

        this.fhirApi
          .search('Organization', {
            _id: organizationIds.join(','),
            _include: 'Organization:partof',
          })
          .subscribe((bundle: fhir4.Bundle) => {
            const resources: fhir4.Organization[] = bundle?.entry?.map(
              ({ resource }) => resource as fhir4.Organization,
            );

            forkJoin(
              resources.map((organizationResource: fhir4.Organization) =>
                this.organizationAdapter.fromResource$(organizationResource),
              ),
            ).subscribe((userOrganizationProviders) => {
              this.store.dispatch(
                userOrganizationProvidersFetched({ userOrganizationProviders }),
              );
            });
          });
      });
  }
}
